import { ChevronLeft, ShieldCheck } from 'lucide-react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useAppStore } from '../../app/appStore';
import { useAuthStore } from '../../features/auth/authStore';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { ticketLabel } from '../../shared/raffle/ticketSerial';
import { fetchDrawHistory, fetchMyDrawHistory } from '../../features/sorteios/api';
import { DrawEntriesSheet } from '../../features/sorteios/DrawEntriesSheet';
import { DrawVerifyOverlay } from '../../features/sorteios/DrawVerifyOverlay';
import { RaffleResultDetailSheet } from '../tabs/WalletPage';
import { HistoryLegend } from '../../shared/ui/HistoryLegend';
import { historyLegendView } from '../../shared/game/historyLegend';
import { groupByLocalDay } from '../../shared/milhar/historyDays';
import { hasMineData, historyRow, mineMark, mineRow } from '../../shared/raffle/historyRowView';
import type { DrawHistoryRow, MyDrawRow } from '../../shared/raffle/historyRowView';
import type { RaffleRecentEvent } from '../../features/wallet/api';
import type { MessageKey } from '../../shared/i18n/messages';
import { raffleReadsEnabled, raffleViewerMode } from '../../shared/raffle/viewerMode';
import { localClock, localDayShort } from '../../shared/lib/datetime';
import { fmtMoney } from '../../shared/lib/format';

const PAGE_SIZE = 50;

type HistoryTab = 'all' | 'mine';

/**
 * 往期列表。
 *
 * <p><b>这一屏必须是 flex 列 + 满高</b>（`ds-screen is-column`）。列表用的是
 * `flex-1 min-h-0 overflow-y-auto`，而 `.ds-screen` 本身只管背景色，是 block ——
 * 于是列表拿不到受限高度，被内容撑成整页高，`overflow-y: auto` 永远没有可滚的
 * 余量，body 也不滚：50 期里只有第一屏够得着，下面的全部失联（2026-09-17 PM
 * 反馈「无法下滑」，这个页从 09-13 上线起就是坏的）。Pick 4 那页没事，是因为
 * 它的 `.milhar-screen` 自带这三行。
 */
export function SorteiosHistoryPage() {
  const t = useI18nStore((state) => state.t);
  useI18nStore((state) => state.language);
  const goBack = useAppStore((state) => state.goBack);
  const token = useAuthStore((state) => state.token);
  const guestMode = useAuthStore((state) => state.guestMode);
  const [tab, setTab] = useState<HistoryTab>('all');

  const viewer = raffleViewerMode(token, guestMode);

  return (
    <section className="ds-screen is-column">
      <header className="ds-header shrink-0">
        <button className="icon-button tap" type="button"
          onClick={() => goBack('sorteios')} aria-label={t('back')}>
          <ChevronLeft size={20} aria-hidden="true" />
        </button>
        <h1 className="flex-1 text-[20px] leading-[26px] font-extrabold">
          {t('sorteiosHistoryTitle')}
        </h1>
      </header>

      <div className="milhar-stats-tabs shrink-0" role="tablist">
        <button type="button" role="tab" aria-selected={tab === 'all'}
          className={`milhar-stats-tab tap${tab === 'all' ? ' is-on' : ''}`}
          onClick={() => setTab('all')}>{t('sorteiosHistTabAll')}</button>
        <button type="button" role="tab" aria-selected={tab === 'mine'}
          className={`milhar-stats-tab tap${tab === 'mine' ? ' is-on' : ''}`}
          onClick={() => setTab('mine')}>{t('sorteiosHistTabMine')}</button>
      </div>

      {/* 两个页签各自挂载自己的组件 —— 各有各的查询、各有各的覆盖层。共用一个
          组件再用 if 分叉的话，「全部」的 50 期和「我的」的分页游标会共享一份
          state，切回来时列表位置和已加载页数都会乱。 */}
      {tab === 'all'
        ? <AllDrawsTab viewer={viewer} token={token} />
        : <MyDrawsTab signedIn={viewer === 'player'} token={token} />}
    </section>
  );
}

/** 触底自动取下一页。放在一处，两个页签共用同一套阈值和行为。 */
function useEndlessScroll(
  ref: React.RefObject<HTMLDivElement>,
  opts: { hasNextPage: boolean; isFetchingNextPage: boolean; fetchNextPage: () => void; rowCount: number },
) {
  const { hasNextPage, isFetchingNextPage, fetchNextPage, rowCount } = opts;
  useEffect(() => {
    const node = ref.current;
    if (!node || !hasNextPage || isFetchingNextPage) return;
    // IntersectionObserver 而不是滚动事件：后者要自己算容器高度和阈值，
    // 横竖都会在某个机型上差几像素导致永远不触发。
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) fetchNextPage();
    }, { rootMargin: '300px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref, hasNextPage, isFetchingNextPage, fetchNextPage, rowCount]);
}

function AllDrawsTab({ viewer, token }: { viewer: ReturnType<typeof raffleViewerMode>; token: string | null }) {
  const t = useI18nStore((state) => state.t);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [verifyId, setVerifyId] = useState<string | null>(null);
  const sentinel = useRef<HTMLDivElement | null>(null);

  // 开奖结果属于公开的公平性记录，接口对访客也开（按所选国家）。
  const query = useInfiniteQuery({
    queryKey: ['draws-history', token || 'guest'],
    enabled: raffleReadsEnabled(viewer),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => fetchDrawHistory(token, pageParam, PAGE_SIZE),
    getNextPageParam: (last) => last.next_cursor || null,
    staleTime: 60_000,
  });

  const events = (query.data?.pages || []).flatMap((page) => page.draws || []);
  const railOn = hasMineData(events);
  const rows = events.map((event) => ({
    ...historyRow(event),
    mine: mineMark(event),
  }));
  const groups = groupByLocalDay(rows);
  const detailEvent = events.find((e) => String(e.event_id || e.id || '') === detailId);
  const verifyEvent = events.find((e) => String(e.event_id || e.id || '') === verifyId);
  // 验算页第 3 步会自己独立算一遍号码；这里把这一行已经算好的权威值原样带过去
  // 做对照，两边永远该是同一个数（同一个 D、同一个 N）——不重算、只传值。
  const verifyRow = rows.find((r) => r.eventId === verifyId);
  const legend = historyLegendView({
    signedIn: viewer === 'player', railOn, jackpot: false,
    rowCount: rows.length, window: PAGE_SIZE,
  });
  useEndlessScroll(sentinel, { ...query, hasNextPage: !!query.hasNextPage, rowCount: rows.length });

  return (
    <>
    <div className={`flex-1 min-h-0 overflow-y-auto no-scrollbar srt-hist${railOn ? '' : ' is-rail-off'}`}>
      {query.isError && rows.length === 0 ? (
        // 与 SorteiosPage 同一套文案键：请求失败和「真的还没有历史」是两件事，
        // 说成一句话就是把 401/500 悄悄伪装成「什么都没发生过」。
        <p className="srt-hist-empty">{t('sorteiosLoadError')}</p>
      ) : query.isPending ? (
        <div className="milhar-stats-skeleton">
          {Array.from({ length: 8 }, (_, i) => <span key={i} className="sk" />)}
        </div>
      ) : rows.length === 0 ? (
        <p className="srt-hist-empty">{t('sorteiosHistoryEmpty')}</p>
      ) : (
        <>
          <HistoryLegend items={legend.items} noRecent={legend.noRecent}
            noRecentKey="sorteiosHistNoneRecent" />
          {/* 表头是给眼睛的列标，不是 ARIA 表格 —— 行已经是按钮，把它们同时
              声明成 table/row/cell 会让读屏器把一个可点的行念成一个不可点的
              单元格。与 Pick 4 的 History 逐字同构。 */}
          <div className="srt-hist-head" aria-hidden="true">
            {railOn ? <span className="srt-hist-head-rail" /> : null}
            <span className="srt-hist-head-time">{t('sorteiosHistHeadTime')}</span>
            <span className="srt-hist-head-ticket">{t('sorteiosHistHeadTicket')}</span>
            <span className="srt-hist-head-pool">{t('sorteiosHistHeadPool')}</span>
            <span />
          </div>
          {groups.map((group) => (
            <Fragment key={group.key}>
              {group.at > 0 ? <h3 className="srt-hist-day">{localDayShort(group.at)}</h3> : null}
              {group.rows.map((row) => (
                <AllDrawRow key={row.eventId} row={row} railOn={railOn}
                  onOpen={() => setDetailId(row.eventId)}
                  onVerify={() => setVerifyId(row.eventId)} />
              ))}
            </Fragment>
          ))}
          <PagingFoot sentinel={sentinel} query={query} />
        </>
      )}
    </div>
    {detailEvent ? (
      <DrawEntriesSheet event={detailEvent} onClose={() => setDetailId(null)} />
    ) : null}
    {verifyEvent ? (
      <DrawVerifyOverlay event={verifyEvent} rowSerial={verifyRow?.drawnSerial ?? null}
        onClose={() => setVerifyId(null)} />
    ) : null}
    </>
  );
}

/**
 * 「全部」页签的一行。
 *
 * <p>与 Pick 4 的 History 行同一套栅格（期号 / 结果 / 时间 / 金额 / 盾牌），
 * 两页看起来必须是一家的（2026-09-17 PM 要求）。开出的号占「结果」那一格，
 * 对应 Pick 4 的四五个数字格。中奖人数移到点开的参与名单里 —— 一行塞不下，
 * 而它也不是扫列表时要比对的东西。
 */
function AllDrawRow({ row, railOn, onOpen, onVerify }: {
  row: DrawHistoryRow & { mine: 'won' | 'played' | 'none' };
  railOn: boolean;
  onOpen: () => void;
  onVerify: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  const time = row.drawnAt > 0 ? localClock(row.drawnAt) : '';
  const body = (
    <>
      {/* 一期由「这一天 + 这个时刻」认（每小时一期，一个国家一个节目）——
          期号不再显示：它和票号都是「#三位数」，同屏出现没人分得清，而且它
          借的是房间五分钟一局的编号，每小时跳 12，本身也读不出顺序
          （2026-09-18 PM）。要一个可引用的技术号时，验算页有 global 号。 */}
      <span className="srt-hist-time">{time}</span>
      <span className="srt-hist-result">
        {row.voided ? (
          <em className="srt-hist-void">{t('sorteiosHistoryVoidedShort')}</em>
        ) : row.drawnSerial !== null ? (
          // drawnSerial 为 null 时整格留空 —— 不许拿 000 冒充一个开奖结果。
          <b className="srt-ticket is-drawn">{ticketLabel(row.drawnSerial)}</b>
        ) : null}
      </span>
      <span className="srt-hist-pool">{row.voided ? '—' : fmtMoney(row.pool)}</span>
    </>
  );
  return (
    <div className={`srt-hist-row${row.voided ? ' is-void' : ''}`}>
      {railOn ? <span className={`srt-hist-rail is-${row.mine}`} aria-hidden="true" /> : null}
      {/* 流拍的期行身不可点：票退了、名单不构成记录，点开是一张空表。 */}
      {row.voided ? (
        <div className="srt-hist-main is-static">{body}</div>
      ) : (
        <button type="button" className="srt-hist-main tap" onClick={onOpen}
          aria-label={[time, t('sorteiosHistoryWho')].filter(Boolean).join(' · ')}>
          {body}
        </button>
      )}
      <VerifyCell drawn={!row.voided} label={t('sorteiosHistoryVerify')} onClick={onVerify} />
    </div>
  );
}

/**
 * 行尾的盾牌。
 *
 * <p><b>每一行都有，没开奖的置灰。</b> 2026-09-17 PM 要求 —— 原先流拍/未开奖
 * 的行整个不渲染这一格，右边缘就成了锯齿状，而「这一行为什么没有盾牌」本身
 * 又没人解释。置灰同时回答了两件事：这一格是有的，只是这一期还没有东西可验。
 *
 * <p>置灰的是 `disabled` 的真按钮，不是一个长得像按钮的 span —— 读屏器要能
 * 念出「不可用」，而不是遇到一个没有角色的图形。
 */
function VerifyCell({ drawn, label, onClick }: { drawn: boolean; label: string; onClick: () => void }) {
  return (
    <button type="button" className={`srt-hist-verify tap${drawn ? '' : ' is-off'}`}
      disabled={!drawn} aria-label={label} onClick={drawn ? onClick : undefined}>
      <ShieldCheck size={15} aria-hidden="true" />
    </button>
  );
}

function PagingFoot({ sentinel, query }: {
  sentinel: React.RefObject<HTMLDivElement>;
  query: { hasNextPage?: boolean; isFetchingNextPage: boolean; isError: boolean; fetchNextPage: () => void };
}) {
  const t = useI18nStore((state) => state.t);
  return (
    <div ref={sentinel} className="srt-mine-more">
      {query.isFetchingNextPage ? (
        <div className="milhar-stats-skeleton">
          {Array.from({ length: 3 }, (_, i) => <span key={i} className="sk" />)}
        </div>
      ) : query.isError ? (
        // 后续页失败不清空已加载的：翻到第 4 页断网，把前 3 页也收走是在惩罚玩家。
        <button type="button" className="srt-mine-retry tap"
          onClick={() => query.fetchNextPage()}>{t('sorteiosMineMoreError')}</button>
      ) : query.hasNextPage ? null : (
        <span className="srt-mine-end">{t('sorteiosMineEnd')}</span>
      )}
    </div>
  );
}

/**
 * 「我的」页签：我参与过的全部期，按游标往前翻。
 *
 * <p>与「全部」页签的三处不同，都是有意的：
 * <ol>
 *   <li><b>不画竖条。</b> 这里每一行都是「我参与的」，竖条只剩两三态，而结果
 *       右边已经用状态和金额写清楚了 —— 再加一根条是同一件事说两遍。</li>
 *   <li><b>点行开的是当事人视角的结果面板</b>（`RaffleResultDetailSheet`），
 *       不是参与名单。我自己的那一期，我想先看到的是我的票中没中。</li>
 *   <li><b>包含还没开的期。</b> 我报名了、还没跑的那一期排在最上面，状态写
 *       「未开奖」，盾牌置灰。</li>
 * </ol>
 */
function MyDrawsTab({ signedIn, token }: { signedIn: boolean; token: string | null }) {
  const t = useI18nStore((state) => state.t);
  const setRoute = useAppStore((state) => state.setRoute);
  const user = useAuthStore((state) => state.user);
  const [resultId, setResultId] = useState<string | null>(null);
  // 验算覆盖层归这个页签自己管：它要用的是「我的」这份数据里的那一期，
  // 外层「全部」只加载了最近几页，翻到更早的期时那里根本没有这一行。
  const [verifyId, setVerifyId] = useState<string | null>(null);
  const sentinel = useRef<HTMLDivElement | null>(null);

  const query = useInfiniteQuery({
    queryKey: ['draws-history-mine', token || 'guest'],
    // 访客不发请求 —— 这个接口对没登录的人是 401，发出去只会换回一个错误页。
    enabled: signedIn,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => fetchMyDrawHistory(token, pageParam, PAGE_SIZE),
    getNextPageParam: (last) => last.next_cursor || null,
    staleTime: 60_000,
  });

  const pages = query.data?.pages || [];
  const events = pages.flatMap((page) => page.draws || []);
  // 汇总只认第一页 —— 它描述的是全部历史，不是已加载的这几页。把页面上的行
  // 加起来的话，每翻一页数字都会往上跳，玩家会以为自己刚又中了一期。
  const summary = pages[0]?.summary || null;
  const rows: MyDrawRow[] = events
    // my_entries 为空的期不该出现在这个接口里；真出现了就丢掉，不渲染「0 张票」。
    .filter((event) => (event.my_entries || []).length > 0)
    .map((event) => mineRow(event));
  const groups = groupByLocalDay(rows);
  const resultEvent = events.find((e) => String(e.event_id || e.id || '') === resultId);
  const verifyEvent = events.find((e) => String(e.event_id || e.id || '') === verifyId);
  const verifyRow = rows.find((r) => r.eventId === verifyId);
  useEndlessScroll(sentinel, { ...query, hasNextPage: !!query.hasNextPage, rowCount: rows.length });

  if (!signedIn) {
    return (
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar srt-hist">
        <p className="srt-hist-empty">{t('sorteiosMineGuest')}</p>
      </div>
    );
  }

  return (
    <>
    <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar srt-hist is-rail-off is-mine">
      {/* 第一页失败就是整个页签失败 —— 不回落到「全部」：玩家点了「我的」，
          给他看别人的期是答非所问。 */}
      {query.isError && rows.length === 0 ? (
        <div className="srt-mine-fallback">
          <p>{t('sorteiosLoadError')}</p>
          <button type="button" className="srt-mine-retry tap"
            onClick={() => query.refetch()}>{t('milharStatsRetry')}</button>
        </div>
      ) : query.isPending ? (
        <div className="milhar-stats-skeleton">
          {Array.from({ length: 8 }, (_, i) => <span key={i} className="sk" />)}
        </div>
      ) : rows.length === 0 ? (
        <div className="srt-mine-empty">
          <p>{t('sorteiosMineEmpty')}</p>
          <button type="button" className="srt-mine-retry tap"
            onClick={() => setRoute('sorteios')}>{t('sorteiosMineEmptyCta')}</button>
        </div>
      ) : (
        <>
          {summary ? (
            <p className="srt-mine-summary">
              {t('sorteiosMineSummary')
                .replace('%e%', String(Number(summary.events ?? 0)))
                .replace('%w%', String(Number(summary.won ?? 0)))
                .replace('%v%', fmtMoney(Number(summary.prize_total ?? 0)))}
            </p>
          ) : null}
          <div className="srt-hist-head" aria-hidden="true">
            <span className="srt-hist-head-time">{t('sorteiosHistHeadTime')}</span>
            <span className="srt-hist-head-ticket">{t('sorteiosMineHeadStatus')}</span>
            <span className="srt-hist-head-pool">{t('sorteiosMineHeadPrize')}</span>
            <span />
          </div>
          {groups.map((group) => (
            <Fragment key={group.key}>
              {group.at > 0 ? <h3 className="srt-hist-day">{localDayShort(group.at)}</h3> : null}
              {group.rows.map((row) => (
                <MyDrawRowItem key={row.eventId} row={row}
                  onOpen={() => setResultId(row.eventId)}
                  onVerify={() => setVerifyId(row.eventId)} />
              ))}
            </Fragment>
          ))}
          <PagingFoot sentinel={sentinel} query={query} />
        </>
      )}
    </div>
    {resultEvent ? (
      <RaffleResultDetailSheet
        event={resultEvent as RaffleRecentEvent}
        userId={user?.id}
        // 回看一期旧结果不该再放一次开奖动画 —— 那是「正在开」的时刻才有的东西。
        reveal={false}
        onClose={() => setResultId(null)}
        onTickets={() => { setResultId(null); setRoute('wallet.tickets'); }}
        onRules={() => { setResultId(null); setRoute('rules'); }}
        onVerifyAnchor={() => { const id = resultId; setResultId(null); setVerifyId(id); }}
        onOpenProfile={() => {}}
      />
    ) : null}
    {verifyEvent ? (
      <DrawVerifyOverlay event={verifyEvent} rowSerial={verifyRow?.drawnSerial ?? null}
        onClose={() => setVerifyId(null)} />
    ) : null}
    </>
  );
}

const STATUS_KEY: Record<MyDrawRow['outcome'], MessageKey> = {
  won: 'sorteiosMineStatusWon',
  lost: 'sorteiosMineLost',
  refunded: 'sorteiosMineRefunded',
  pending: 'sorteiosMineStatusPending',
};

/**
 * 「我的」页签的一行。
 *
 * <p><b>状态是状态，金额是金额</b>（2026-09-17 PM 要求）。原先这两件事挤在
 * 同一格：有时是「R$ 100」，有时是「未中」，有时是「已退票」—— 同一列里一会
 * 儿是钱一会儿是词，扫下来数不出自己中了几期。现在状态自成一列，金额自成
 * 一列，没有奖金的行金额写「—」而不是留空：留空会被读成「这里的数还没算出来」。
 *
 * <p>票号整串列出、不折叠（PM 要求）—— 第二行铺开，装不下就换行。玩家拿这些
 * 号去对中奖名单，折掉任何一个都可能正是他要找的那个。
 */
function MyDrawRowItem({ row, onOpen, onVerify }: {
  row: MyDrawRow;
  onOpen: () => void;
  onVerify: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  const time = row.drawnAt > 0 ? localClock(row.drawnAt) : '';
  const status = t(STATUS_KEY[row.outcome]);
  const ticketCountLabel = row.ticketCount === 1
    ? t('sorteiosMineTicketsOne')
    : t('sorteiosMineTickets').replace('%n%', String(row.ticketCount));
  const body = (
    <>
      <span className="srt-hist-time">{time}</span>
      <span className={`srt-mine-status is-${row.outcome}`}>{status}</span>
      <span className={`srt-hist-pool${row.outcome === 'won' ? '' : ' is-none'}`}>
        {row.outcome === 'won' ? fmtMoney(row.prize) : '—'}
      </span>
      <span className="srt-mine-tickets">
        <em>{ticketCountLabel}</em>
        {row.tickets.map((ticket) => (
          <b key={ticket.serial} className={`srt-ticket${ticket.won ? ' is-win' : ''}`}>
            {ticketLabel(ticket.serial)}
          </b>
        ))}
      </span>
    </>
  );
  const drawn = row.outcome === 'won' || row.outcome === 'lost';
  return (
    <div className={`srt-hist-row${row.voided ? ' is-void' : ''}`}>
      {/* 流拍和未开奖的行身仍然可点 —— 那一期我的票是真的，结果面板要能打开
          看到自己买了哪些号。不可点的只有验算，因为确实还没有号可验。 */}
      <button type="button" className="srt-hist-main tap" onClick={onOpen}
        aria-label={[time, status, ticketCountLabel].filter(Boolean).join(' · ')}>
        {body}
      </button>
      <VerifyCell drawn={drawn} label={t('sorteiosHistoryVerify')} onClick={onVerify} />
    </div>
  );
}
